"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Briefcase,
  Sparkles,
  Stethoscope,
  Crown,
  Brain,
  Mic,
  ArrowRight,
  Target,
  Users,
  Layers,
  Activity,
  Compass,
  Award,
  CheckCircle2,
  Lightbulb,
  Building2,
  Clock,
  Scale,
  GitBranch,
  TrendingUp,
  ShieldCheck,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { ArchitectureDiagramBlock } from "@/lib/blocks/architecture-diagram-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

const DOMAINS = [
  {
    icon: Activity,
    title: "Operational Excellence System Design",
    body: "Building the infrastructure that produces consistent, measurable outcomes — protocols, checklists, decision frameworks, accountability structures, and the organizational discipline to sustain them. Operational excellence approached as a continuous practice, not a destination.",
    accent: "red",
  },
  {
    icon: Crown,
    title: "Leadership Methodology Implementation",
    body: "Translating the clinical method into the leadership team&apos;s daily practice — diagnostic discipline, triage framework, protocol-driven execution, outcome accountability. Leadership implemented as an operational practice, not a theoretical discipline.",
    accent: "purple",
  },
  {
    icon: Brain,
    title: "Decision Framework Development",
    body: "Replacing intuition with evidence-based systems that produce reliable results under pressure. The diagnostic precision of medicine applied to organizational decision-making — data, protocols, and evidence driving decisions, not experience or hierarchy.",
    accent: "cyan",
  },
  {
    icon: Stethoscope,
    title: "Organizational Diagnosis and Intervention",
    body: "The clinical method applied to organizational structure — identifying not just symptoms but root causes, not just challenges but structural vulnerabilities, not just gaps but the systemic patterns that created them. Evidence-based intervention with operational continuity maintained throughout.",
    accent: "emerald",
  },
  {
    icon: Scale,
    title: "Execution Discipline and Accountability",
    body: "Ensuring the plan translates into action and the action produces results. Accountability structures that sustain execution over time — mechanisms that prevent the gap between strategy and execution from widening under pressure.",
    accent: "amber-gold",
  },
];

const STAGES = [
  { num: "01", title: "Intelligence", body: "A forensic examination of the organizational landscape — the diagnostic phase, non-negotiable. Maps the strategic objectives, operational capabilities, governance structures, execution gaps, cultural dynamics, and stakeholder ecosystem with clinical precision." },
  { num: "02", title: "Analysis", body: "Transforms intelligence into diagnosis — identifying root causes, structural vulnerabilities, and the systemic patterns that perpetuate underperformance. Differential diagnosis, evidence-based reasoning, and protocol-driven treatment planning." },
  { num: "03", title: "Strategy", body: "Designs the intervention — specific operating mechanisms that address the diagnosed gaps. Not a vision document. An implementation blueprint grounded in verified data: the protocols, checklists, decision frameworks, and accountability structures to be deployed." },
  { num: "04", title: "Orchestration", body: "Coordinates implementation across all relevant teams and systems. Ensures the advisory produces systemic transformation — every team aligned, every protocol deployed, every accountability structure activated. The compartmentalized architecture protects sovereign-level work." },
  { num: "05", title: "Amplification", body: "Scales what works across the organization. The advisory does not produce pilot programs that never scale. It produces mechanisms that propagate — protocols adopted because they work, checklists that persist because they prevent failure." },
  { num: "06", title: "Feedback", body: "Measures outcomes and adapts the approach continuously. The patient either recovers or does not. The feedback stage produces verified data on execution speed improvement, operational uptime, governance effectiveness, and organizational resilience." },
];

const MODELS = [
  {
    icon: Clock,
    title: "Retainer Advisory",
    body: "Ongoing operational counsel through weekly or biweekly sessions with diagnostic reviews, system implementation, and outcome measurement. Typically runs six to twelve months for initial implementation, with ongoing retainer relationships for sustained development.",
    accent: "red",
  },
  {
    icon: GitBranch,
    title: "Project Advisory",
    body: "Defined engagements with specific deliverables — operational assessments, governance transformations, crisis response programs, organizational restructuring. Concludes when the deliverable is verified and the outcome is measured — not when the hours are exhausted.",
    accent: "purple",
  },
  {
    icon: Lightbulb,
    title: "Intensive Advisory",
    body: "Concentrated, high-impact advisory over compressed timeframes — typically three to five days of immersive engagement. Designed for organizations facing urgent operational challenges, critical transitions, or crisis conditions where the operating method must be deployed rapidly.",
    accent: "cyan",
  },
];

const DIMENSIONS = [
  { label: "Organizational Context", body: "Strategic objectives, competitive landscape, market position, regulatory environment, stakeholder ecosystem." },
  { label: "Operational Capabilities", body: "Existing infrastructure, processes, and competencies — what the organization can do today and the gap to required capability." },
  { label: "Governance Structures", body: "Decision-making architecture — who decides, how decisions are made, what information informs them, and how they are implemented." },
  { label: "Execution Gaps", body: "The space between strategy and execution — where the plan is sound but the operation falters. Root causes, not just symptoms." },
  { label: "Cultural Dynamics", body: "Norms, behaviors, and incentive structures that shape how people work. Culture as a structural force, not a soft variable." },
  { label: "Team Composition", body: "People who execute the work — skills, experience, alignment with objectives. The 70% Operating Model lens applied." },
  { label: "Risk Landscape", body: "Threats, vulnerabilities, and exposures that could disrupt operations — the clinical method applied to organizational risk." },
];

const GAINS = [
  { icon: Layers, title: "Protocols", body: "Documented procedures that teams follow when facing recurring operational challenges. Replace ad hoc decision-making with consistent, evidence-based action — protocols that persist beyond the engagement itself.", accent: "red" },
  { icon: CheckCircle2, title: "Checklists", body: "Mechanisms that prevent failure by ensuring critical steps are not skipped under pressure. Operational insurance — the kind that has produced zero security incidents across fifteen-plus years of sovereign-level engagements.", accent: "purple" },
  { icon: Brain, title: "Decision Frameworks", body: "Evidence-based systems that produce reliable results under pressure — replacing intuition with methodology, instinct with protocol, hierarchy with evidence. Decision-making infrastructure that produces consistent outcomes.", accent: "cyan" },
  { icon: Activity, title: "Operational Architecture", body: "The integrated system that ties protocols, checklists, decision frameworks, and accountability structures into a coherent operating infrastructure. The architecture that produces excellence as a default, not an exception.", accent: "emerald" },
  { icon: Users, title: "Leadership Capability", body: "The leadership team emerges from the advisory with embedded operational literacy — the diagnostic precision, triage discipline, and protocol-driven execution internalized as daily practice.", accent: "amber-gold" },
  { icon: Award, title: "Outcome Evidence", body: "Verified data on execution speed, operational uptime, governance effectiveness, and organizational resilience — measured against benchmarks established during the diagnostic phase.", accent: "denim" },
];

export default function ExecutiveAdvisoryPage() {
  return (
    <>
      <PageHero
        icon={Briefcase}
        overline="Service · Executive Advisory"
        overlineClassName="text-purple"
        title={
          <>
            Embedding the Operating Method <span className="text-gradient-premium">in Leadership Teams.</span>
          </>
        }
        subtitle="Ongoing operational counsel for C-suite executives, founders, and leadership teams. The operating method embedded within organizations whose challenges demand sovereign-scale operational expertise — systems over decks, evidence over intuition, outcomes over effort."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Services", href: "/services/" }, { label: "Executive Advisory" }]}
        badge="Systems, not slide decks"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-5`}>
              <Sparkles className="size-3.5 text-purple" />
              <span className="text-overline text-purple">The Thesis</span>
            </div>
            <h2 className="text-display-md mb-5">
              Most executive advisors offer perspective. Dr. Jyoti Kush offers infrastructure.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The advisory produces tangible operating mechanisms — the protocols that teams follow, the checklists that prevent failure, the decision frameworks that produce consistent outcomes, and the accountability structures that ensure execution. These mechanisms persist beyond the advisory relationship itself. The organization does not become dependent on the advisor. It becomes capable of operating independently, with the operating method embedded in its organizational DNA.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The advisory is informed by sovereign-scale experience — operations where the stakes are national, the margin for error is zero, and the consequences of failure are not theoretical. The same clinical discipline that governs medical practice governs every advisory engagement: diagnostic precision, evidence-based intervention, protocol-driven execution, outcome measurement.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              No engagement is prescribed without diagnosis. No scope is defined without understanding the landscape. The diagnostic phase maps the organizational reality with forensic precision — identifying not just symptoms but root causes, not just challenges but structural vulnerabilities.
            </p>
          </div>

          <div className="space-y-3">
            {[
              { num: "01", title: "Operating Systems, Not Advice", body: "Infrastructure, not recommendations. Mechanisms, not observations. The deliverable persists beyond the engagement itself.", accent: "red" },
              { num: "02", title: "The Clinical Method Applied", body: "The physician&apos;s discipline — diagnosis, triage, protocol, judgment, outcome accountability — applied to organizational leadership.", accent: "purple" },
              { num: "03", title: "Outcome Accountability", body: "The engagement is measured by results, not effort. The same standard that governs medical practice governs every advisory.", accent: "cyan" },
              { num: "04", title: "Discretion as Foundation", body: "National security level confidentiality, binding agreements, compartmentalized operations. The structural foundation of every engagement.", accent: "emerald" },
            ].map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.num}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 ring-2 ${a.ring}`}>
                      <span className="text-caption font-bold text-white">{p.num}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{p.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Target className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">What Advisory Covers</span>
          </div>
          <h2 className="text-display-md mb-4">Five interconnected domains.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The advisory addresses the complete landscape of operational leadership — each domain approached with clinical precision, sovereign-scale experience, and outcome accountability.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {DOMAINS.map((d, i) => {
            const a = ACCENT[d.accent];
            return (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <d.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-md mb-3 ${a.text}`}>{d.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">{d.body}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <TrendingUp className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">The Six-Stage Approach</span>
          </div>
          <h2 className="text-display-md mb-4">The methodology refined through sovereign-scale operations.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each stage is grounded in the clinical method. The stages are sequential in logic but iterative in practice — the loop never closes, it accelerates as the operating method compounds.
          </p>
        </div>
        <div className="space-y-3">
          {STAGES.map((s, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim"];
            const a = ACCENT[accents[i]];
            return (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 shadow-lg ring-2 ${a.ring} group-hover:scale-110 transition-all`}>
                    <span className="text-body-md font-bold text-white">{s.num}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{s.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
              <Stethoscope className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">The Diagnostic Phase</span>
            </div>
            <h2 className="text-display-md mb-5">Understanding before prescribing.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The diagnostic phase is the foundation of every advisory engagement. It is not optional. It is not administrative. It is operational — the clinical method applied to the engagement itself. The diagnostic phase determines whether the advisory is appropriate and whether the engagement can produce the verified outcomes that both parties require.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The diagnostic conversation typically spans 60 to 90 minutes, conducted with the same confidentiality standards that govern sovereign-level engagements. The conversation produces a diagnostic report — a comprehensive map of the organizational landscape with identified gaps, root causes, and recommended interventions.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {DIMENSIONS.map((d, i) => {
              const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim", "lime"];
              const a = ACCENT[accents[i % accents.length]];
              return (
                <motion.div
                  key={d.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border}`}
                >
                  <h3 className={`text-heading-sm mb-2 ${a.text}`}>{d.label}</h3>
                  <p className="text-caption text-muted-foreground leading-relaxed">{d.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Building2 className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Engagement Models</span>
          </div>
          <h2 className="text-display-md mb-4">Three structures, one operating standard.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each model is designed to match the nature of the challenge, the duration of the relationship, and the operational objectives. All begin with a diagnostic conversation. No engagement is prescribed without diagnosis.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {MODELS.map((m, i) => {
            const a = ACCENT[m.accent];
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                  <m.icon className="size-7 text-white" />
                </div>
                <h3 className={`text-heading-lg mb-3 ${a.text}`}>{m.title}</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">{m.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Award className="size-3.5 text-lime" />
            <span className="text-overline text-lime">What Clients Gain</span>
          </div>
          <h2 className="text-display-md mb-4">Operating systems, not advice.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The advisory produces tangible, persistent operational mechanisms — the infrastructure that transforms how organizations execute, govern, and perform. Organizations gain not advice but infrastructure.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {GAINS.map((g, i) => {
            const a = ACCENT[g.accent];
            return (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <g.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{g.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{g.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Compass className="size-3.5 text-red" />
            <span className="text-overline text-red">Advisory Engagement Map</span>
          </div>
          <h2 className="text-display-md mb-4">One operating method, six interconnected satellites.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The advisory is anchored in the clinical method and extends outward into six operational domains. Each satellite connects to the center and to the others — the architecture is integrated, not siloed.
          </p>
        </div>
        <ArchitectureDiagramBlock
          center={{
            label: "The Clinical Method",
            body: "Diagnosis. Triage. Protocol. Judgment. Outcome accountability. The operating system of a physician, applied to leadership.",
            icon: <Stethoscope className="size-5" />,
          }}
          satellites={[
            { label: "Strategy Governance", body: "Evaluating strategic proposals with operational rigor and evidence-based reasoning.", icon: <Target className="size-4" />, accent: "red" },
            { label: "Execution Architecture", body: "Protocols, checklists, decision frameworks — the infrastructure that turns plans into verified outcomes.", icon: <Activity className="size-4" />, accent: "purple" },
            { label: "Talent Operating Model", body: "The 70% Operating Model applied to recruitment, integration, and team composition.", icon: <Users className="size-4" />, accent: "emerald" },
            { label: "Risk Discipline", body: "Clinical risk identification, evidence-based triage, protocol-driven response, continuous monitoring.", icon: <ShieldCheck className="size-4" />, accent: "cyan" },
            { label: "Decision Frameworks", body: "Replacing intuition with evidence-based systems that produce reliable results under pressure.", icon: <Brain className="size-4" />, accent: "amber-gold" },
            { label: "Accountability Structures", body: "Mechanisms that sustain execution over time and prevent strategy-to-execution gaps from widening.", icon: <Scale className="size-4" />, accent: "denim" },
          ]}
          connections={[
            { from: -1, to: 0 },
            { from: -1, to: 1 },
            { from: -1, to: 2 },
            { from: -1, to: 3 },
            { from: -1, to: 4 },
            { from: -1, to: 5 },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Award className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Advisor Credentials</span>
          </div>
          <h2 className="text-display-md mb-4">The credentials behind the advisory.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The advisory draws from operational reality — credentials earned through building and running sovereign-grade systems, not through consulting engagements or academic appointments.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "MBBS in Gynaecology, Delhi", body: "The physician's training that grounds the operating method in clinical discipline. Diagnosis, triage, protocol, outcome accountability — the disciplines of medicine, transferred to organizational leadership.", accent: "red" },
            { title: "COO of a Sovereign-Grade Conglomerate", body: "Chief Operating Officer of an organization operating across eighteen countries, three continents, with nine proprietary platforms and over nine hundred million citizens served.", accent: "purple" },
            { title: "99.9999% Operational Uptime", body: "Maximum downtime of 31.5 seconds per year. The operational standard against which all advisory engagements are calibrated — and the benchmark the advisory aims to install in every client.", accent: "cyan" },
            { title: "Zero Security Incidents", body: "Across fifteen-plus years of sovereign-level engagements. The discretion doctrine operationalized — security built into architecture, not added through policy.", accent: "emerald" },
            { title: "100% Client Satisfaction", body: "Verified satisfaction across more than three hundred elite clients served. The advisory inherits the same outcome-accountability standard.", accent: "amber-gold" },
            { title: "Present at Nobel and WEF", body: "Present at the Nobel Peace Prize and the World Economic Forum. Worked alongside governments and global leaders in over thirty countries. The sovereign-scale credibility is established, not claimed.", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <Lightbulb className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Frequently Asked</span>
          </div>
          <h2 className="text-display-md mb-4">Questions organizations ask before engaging.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The advisory is curated, not marketed. The diagnostic conversation covers everything the FAQ cannot — and produces the answer to questions organizations have not yet formulated.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "What is the typical duration of an advisory engagement?", a: "Retainer advisory typically runs six to twelve months for initial implementation, with ongoing retainer relationships for sustained development. Project advisory concludes when the deliverable is verified and outcome measured. Intensive advisory is concentrated over three to five days. Duration is calibrated to the diagnostic data, not to a predetermined timeline." },
            { q: "How does the diagnostic phase work?", a: "The diagnostic conversation typically spans 60 to 90 minutes, conducted with the same confidentiality standards that govern sovereign-level engagements. The conversation produces a diagnostic report — a comprehensive map of the organizational landscape with identified gaps, root causes, and recommended interventions. No engagement is prescribed without diagnosis." },
            { q: "What does the advisory cost?", a: "Engagement fees are discussed during the diagnostic phase, tailored to the engagement model, scope, duration, and outcome requirements. Every advisory is customized — there are no standard packages. The diagnostic conversation determines scope and fee structure simultaneously." },
            { q: "Can the advisory be combined with other services?", a: "Yes. The advisory frequently combines with corporate workshops for team-level implementation, executive mentorship for individual leader development, and operational consulting for organization-wide transformation. The combinations are designed around the diagnostic data and the outcomes the organization seeks." },
            { q: "How is success measured?", a: "Outcomes are measured against benchmarks established during the diagnostic phase. The metrics include execution speed improvement, operational uptime, governance effectiveness, and organizational resilience. The measurement framework mirrors the accountability standard that governs medical practice: the patient either recovers or does not." },
            { q: "What happens after the advisory concludes?", a: "The advisory produces mechanisms that persist beyond the engagement itself — protocols, checklists, decision frameworks, accountability structures. The organization does not become dependent on the advisor. It becomes capable of operating independently, with the operating method embedded in its organizational DNA. Optional follow-up retainers are available for sustained development." },
            { q: "Is the advisory available to organizations outside sovereign-grade operations?", a: "The advisory is available to any leadership team whose challenges demand the operating method. Sovereign-scale experience is brought to every engagement regardless of the client's current scale. The diagnostic conversation determines alignment between the challenge and the operating method." },
            { q: "How is confidentiality handled?", a: "National security level confidentiality governs every advisory engagement. Binding non-disclosure agreements are tailored to the specific sensitivity level. The compartmentalized architecture ensures every participant sees only what they need to see. The unified command sees everything." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-purple/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-cyan/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Briefcase className="size-10 text-purple mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Inquire about executive advisory.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Every advisory relationship begins with a diagnostic conversation — the clinical method applied to the engagement itself. The conversation determines whether the engagement is appropriate and whether the advisory can produce the verified outcomes that both parties require.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Begin Advisory
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/services/board-service/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Crown className="size-4" />
                Board Service
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Board Service", description: "Sovereign-grade governance oversight at the board level.", href: "/services/board-service/", icon: Crown, accent: "cyan" },
          { title: "Executive Mentorship", description: "One-on-one leadership development. The operating method transmitted personally.", href: "/services/executive-mentorship/", icon: Users, accent: "emerald" },
          { title: "Operational Consulting", description: "Sovereign-scale operational transformation across organizations.", href: "/services/operational-consulting/", icon: Activity, accent: "amber-gold" },
          { title: "All Services", description: "Ten engagement types across six service modalities. One operating method.", href: "/services/", icon: Compass, accent: "purple" },
        ]}
      />
    </>
  );
}
